import axios from "axios";

export const formatDateToLocal = (date, { dateOnly, timeOnly } = {}) => {
  if (!date) return null;

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return null;

  if (timeOnly) {
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )} ${ampm}`;
  }

  if (dateOnly) {
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  }

  return dateObj.toLocaleString("en-GB");
};

export const parseLocalDateFromInput = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);

  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));

  return isNaN(date.getTime()) ? null : date;
};

export const formatDateForInput = (date) => {
  if (!date) return "";

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return "";

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const convertTime12HourTo24Hour = (time12h) => {
  if (!time12h) return "00:00";
  const [time, modifier] = time12h.split(/\s+/);
  let [hours, minutes] = time.split(":");

  if (hours === "12") {
    hours = "00";
  }

  if (modifier?.toUpperCase() === "PM") {
    hours = String(parseInt(hours, 10) + 12);
  }

  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

export const formatDateForEditInput = (date) => {
  if (!date) return "";

  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatTimeForEditInput = (time) => {
  if (!time) return "";

  if (typeof time === "string" && time.includes(":")) {
    return convertTime12HourTo24Hour(time);
  }

  const d = new Date(time);
  if (isNaN(d.getTime())) return "";

  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

export const createDateFromEditInput = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-");

  const dateUTC = new Date(
    Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0, 0)
  );

  return dateUTC;
};

export const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof File)) {
      reject(new Error("Invalid file provided"));
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 800;
        const scaleSize = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas to Blob conversion failed"));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          0.7
        );
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof File)) {
      reject(new Error("Invalid file provided"));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const uploadImageToBlob = async (file, fileName, folder) => {
  try {
    const base64Image = await convertToBase64(file);
    const response = await axios.post(
      `/user/upload-image`,
      {
        file: {
          uri: base64Image,
          mimetype: file.type || "image/jpeg",
        },
        label: fileName,
        imageType: folder,
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    return response;
  } catch (error) {
    throw new Error("Image upload failed: " + error.message);
  }
};

export const formatTicketPrice = (price, currency = "NGN") => {
  const numPrice = typeof price === "string" ? parseFloat(price) : price;

  if (numPrice === 0 || price === "0") {
    return "Free";
  }

  const symbol = currency === "NGN" ? "₦" : "$";
  return `${symbol}${numPrice.toLocaleString()}`;
};

export const formatPrice = (price, currency = "NGN") => {
  const symbol = currency === "USD" ? "$" : "₦";

  if (price === undefined || price === null) {
    return `${symbol}0`;
  }

  const priceStr = price.toString().replace(/[^0-9.]+/g, "");
  const numericPrice = parseFloat(priceStr);

  if (isNaN(numericPrice)) {
    return `${symbol}0`;
  }

  if (numericPrice === 0) {
    return "Free";
  }

  return `${symbol}${numericPrice.toLocaleString("en-US")}`;
};

export const formatDateWithOrdinal = (date) => {
  if (!date) return "Invalid Date";
  const parsedDate = new Date(date);
  if (isNaN(parsedDate)) return "Date TBD";
  const day = parsedDate.getDate();
  const month = parsedDate.toLocaleString("default", { month: "short" });
  const year = parsedDate.getFullYear();
  const ordinalDay = (() => {
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  })();
  return `${ordinalDay} ${month}, ${year}`;
};

export const formatDateLessThanOneDay = (date) => {
  if (!date) return "Invalid Date";

  let parsedDate;

  if (typeof date === "string" && date.includes("T")) {
    parsedDate = new Date(date);
  } else {
    parsedDate = new Date(`${date}T00:00:00`);
  }

  if (isNaN(parsedDate.getTime())) return "Date TBD";

  const localDate = new Date(
    parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000
  );

  const day = localDate.getDate();
  const month = localDate.toLocaleString("default", { month: "short" });
  const year = localDate.getFullYear();

  const ordinalDay = (() => {
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  })();

  return `${ordinalDay} ${month}, ${year}`;
};

export const getEventStatus = (eventDate) => {
  if (!eventDate) return "Upcoming";
  const today = new Date();
  const event = new Date(eventDate);
  const todayDateOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const eventDateOnly = new Date(
    event.getFullYear(),
    event.getMonth(),
    event.getDate()
  );
  if (eventDateOnly > todayDateOnly) return "Upcoming";
  else if (eventDateOnly.getTime() === todayDateOnly.getTime()) return "Live";
  else return "Complete";
};

export const getCurrencySymbol = (currency) => {
  if (!currency) return "₦";

  switch (currency.toUpperCase()) {
    case "USD":
      return "$";
    case "NGN":
      return "₦";
    default:
      return "₦";
  }
};

export const formatDateTime = (date) => {
  if (!date) return "N/A";
  const parsedDate = new Date(date);
  if (isNaN(parsedDate)) return "N/A";
  return parsedDate.toLocaleString("en-GB", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    day: "numeric",
    month: "short",
  });
};

export const formatDateForDisplay = (date) => {
  if (!date) return "";

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return "";

  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();

  return `${day}/${month}/${year}`;
};

export const parseDisplayDateInput = (value) => {
  if (!value) return null;

  const parts = value.split("/");
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));

  return isNaN(date.getTime()) ? null : date;
};

export const formatDateForBackend = (date) => {
  if (!date) return null;

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return null;

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatTimeForBackend = (time) => {
  if (!time) return null;

  const dateObj = time instanceof Date ? time : new Date(time);
  if (isNaN(dateObj.getTime())) return null;

  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )} ${ampm}`;
};

export const sortEventsByDate = (events) => {
  return events.sort((a, b) => {
    const dateA = new Date(
      a.eventDate || a.startDate || a.multipleDates?.[0]?.date
    );
    const dateB = new Date(
      b.eventDate || b.startDate || b.multipleDates?.[0]?.date
    );
    return dateA - dateB;
  });
};
